/**
 * EtagCache.ts
 * ────────────
 * Cache LRU borné mappant {@code key -> { etag , body }} , dimensionné pour la
 * revalidation conditionnelle HTTP des réponses {@code GET /…} servies avec un
 * en-tête {@code ETag}.
 *
 * <p>Pourquoi un cache JS , puisque le browser dispose déjà du cache HTTP ?
 * L'API {@code fetch()} avec {@code mode: cors} et un header
 * {@code Authorization} ne renvoie pas systématiquement {@code If-None-Match}
 * au backend , même si la réponse a {@code Cache-Control: no-cache ,
 * must-revalidate}. Les sous-classes {@code Fetcher} portent donc la
 * revalidation côté JS : on stocke l'ETag + le body au 1er hit , on envoie
 * {@code If-None-Match} au prochain appel , et on restitue le body si le
 * backend répond {@code 304 Not Modified}.
 *
 * <p>Politique d'éviction : double critère LRU stricte
 * <ul>
 *   <li>nombre d'entrées plafonné par {@code maxEntries} ;
 *   <li>taille cumulée plafonnée par {@code maxBytes}.
 * </ul>
 * À chaque hit ( get ) , l'entrée est ré-insérée pour rafraîchir son rang
 * dans l'ordre d'itération de la {@link Map} ( spec ECMAScript : insertion
 * order préservé ). À chaque {@code set} , si la capacité ( entrées ou
 * octets ) est dépassée , les plus anciens sont évincés jusqu'à respect.
 *
 * <p><b>Bornes par défaut</b> : 50 entrées et 20 MB. Dimensionnées
 * conservativement parce que le tab browser entier dispose typiquement
 * de quelques centaines de MB seulement et qu'un cache silencieux ne doit
 * jamais être le coupable d'une saturation perçue par l'utilisateur.
 */
export interface EtagCacheOptions {
  maxEntries?: number;
  /** Capacité maximale en octets. Une entrée au-delà est refusée silencieusement. */
  maxBytes?: number;
}

interface EtagCacheEntry<T> {
  etag: string;
  body: T;
  size: number;
}

export class EtagCache<T> {
  static readonly DEFAULT_MAX_ENTRIES = 50;
  /** 20 MB - borne agressive pour ne pas saturer le heap browser sur sessions longues. */
  static readonly DEFAULT_MAX_BYTES = 20 * 1024 * 1024;

  // Mutables via resize() pour permettre une reconfiguration à chaud
  // ( ex: après lecture de /session/config au boot ). Pas exposés en
  // écriture publique : seule resize() peut les changer , avec validation
  // et éviction immédiate des entrées qui violeraient les nouvelles bornes.
  #maxEntries: number;
  #maxBytes: number;
  readonly #map: Map<string, EtagCacheEntry<T>> = new Map();
  #totalBytes = 0;

  constructor(options: EtagCacheOptions | number = {}) {
    // Compat ascendante : ancien constructeur (maxEntries: number).
    const opts: EtagCacheOptions = typeof options === "number" ? { maxEntries: options } : options;
    const maxEntries = opts.maxEntries ?? EtagCache.DEFAULT_MAX_ENTRIES;
    const maxBytes = opts.maxBytes ?? EtagCache.DEFAULT_MAX_BYTES;
    if (!Number.isInteger(maxEntries) || maxEntries < 1) {
      throw new Error(`EtagCache maxEntries doit être un entier >= 1 , reçu : ${maxEntries}`);
    }
    if (!Number.isFinite(maxBytes) || maxBytes < 1) {
      throw new Error(`EtagCache maxBytes doit être un nombre >= 1 , reçu : ${maxBytes}`);
    }
    this.#maxEntries = maxEntries;
    this.#maxBytes = maxBytes;
  }

  /**
   * Lookup + bump LRU : si l'entrée existe , on la déplace en queue de Map
   * pour qu'elle soit la dernière candidate à l'éviction.
   */
  get(key: string): { etag: string; body: T } | undefined {
    const entry = this.#map.get(key);
    if (entry === undefined) return undefined;
    this.#map.delete(key);
    this.#map.set(key, entry);
    return { etag: entry.etag, body: entry.body };
  }

  /**
   * Stocke ou rafraîchit une entrée. Évince le LRU jusqu'à respect des deux
   * bornes ( entrées et octets ). Une entrée plus grosse que {@code maxBytes}
   * n'est pas stockée ( retour silencieux ) - éviter d'évincer tout le cache
   * pour rien quand un payload anormalement gros arrive.
   *
   * @param sizeBytes taille estimée du body en octets. Si {@code undefined} ,
   *                  recalculée par {@code JSON.stringify(body).length * 2}
   *                  ( UTF-16 ) - cher pour gros payloads donc préférer la
   *                  donnée Content-Length / response.text.length depuis le
   *                  caller.
   */
  set(key: string, etag: string, body: T, sizeBytes?: number): void {
    const size = sizeBytes ?? estimateSize(body);
    if (size > this.#maxBytes) {
      // Refuse l'entrée , supprime tout reste pour ce key.
      const existing = this.#map.get(key);
      if (existing) {
        this.#totalBytes -= existing.size;
        this.#map.delete(key);
      }
      return;
    }

    const existing = this.#map.get(key);
    if (existing) {
      this.#totalBytes -= existing.size;
      this.#map.delete(key);
    }

    while (this.#map.size > 0
           && (this.#map.size >= this.#maxEntries
               || this.#totalBytes + size > this.#maxBytes)) {
      const oldestKey = this.#map.keys().next().value;
      if (oldestKey === undefined) break;
      const oldest = this.#map.get(oldestKey)!;
      this.#totalBytes -= oldest.size;
      this.#map.delete(oldestKey);
    }

    this.#map.set(key, { etag, body, size });
    this.#totalBytes += size;
  }

  /** Vide le cache. Utile en logout / changement d'utilisateur. */
  clear(): void {
    this.#map.clear();
    this.#totalBytes = 0;
  }

  delete(key: string): boolean {
    const entry = this.#map.get(key);
    if (!entry) return false;
    this.#totalBytes -= entry.size;
    return this.#map.delete(key);
  }

  size(): number {
    return this.#map.size;
  }

  totalBytes(): number {
    return this.#totalBytes;
  }

  capacity(): number {
    return this.#maxEntries;
  }

  byteCapacity(): number {
    return this.#maxBytes;
  }

  /**
   * Factory : crée une instance pré-configurée. Plus expressive qu'un
   * appel direct au constructeur quand les options viennent d'une
   * source de config dynamique ( ex: /session/config , feature flags ).
   *
   * @example
   *   EtagCache.fromConfig<MyType>({ maxEntries: 100, maxBytes: 50_000_000 })
   */
  static fromConfig<U>(options: EtagCacheOptions): EtagCache<U> {
    return new EtagCache<U>(options);
  }

  /**
   * Reconfigure les caps à chaud ( ex: après lecture de /session/config
   * au boot ). Évince immédiatement les entrées qui dépasseraient les
   * nouvelles bornes pour ne jamais maintenir un état en violation.
   */
  resize(options: EtagCacheOptions): void {
    if (options.maxEntries !== undefined) {
      if (!Number.isInteger(options.maxEntries) || options.maxEntries < 1) {
        throw new Error(`EtagCache resize maxEntries doit être un entier >= 1`);
      }
      this.#maxEntries = options.maxEntries;
    }
    if (options.maxBytes !== undefined) {
      if (!Number.isFinite(options.maxBytes) || options.maxBytes < 1) {
        throw new Error(`EtagCache resize maxBytes doit être un nombre >= 1`);
      }
      this.#maxBytes = options.maxBytes;
    }
    // Eviction immédiate si la nouvelle borne est plus serrée que l'état courant.
    while (this.#map.size > 0
           && (this.#map.size > this.#maxEntries
               || this.#totalBytes > this.#maxBytes)) {
      const oldestKey = this.#map.keys().next().value;
      if (oldestKey === undefined) break;
      const oldest = this.#map.get(oldestKey)!;
      this.#totalBytes -= oldest.size;
      this.#map.delete(oldestKey);
    }
  }
}

/**
 * Estimation grossière du coût mémoire JS d'un body : on sérialise une fois
 * pour mesurer la taille texte ( UTF-16 dans la plupart des moteurs JS , 2
 * octets par caractère en moyenne ). Sous-estime la mémoire réelle ( objets
 * V8 ont overhead structurel ) mais suffit pour borner.
 */
function estimateSize(body: unknown): number {
  try {
    return JSON.stringify(body).length * 2;
  }
  catch {
    return 1024;
  }
}
